import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Button, Empty, Form, Modal, Skeleton } from "antd";
import { useTranslation } from "react-i18next";

import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";
import uploadFile from "../../../../utilities/uploadFile";
import EmployeeFormFields from "./EmployeeFormFields";
import EmployeePageHeader from "./EmployeePageHeader";
import EmployeePhotoUpload from "./EmployeePhotoUpload";
import EmployeeStatusTag from "./EmployeeStatusTag";
import {
    EMPLOYEE_LIST_PATH,
    applyValidationFieldErrors,
    employeeDetailsPath,
    getEmployeeErrorMessage,
    getEmployeeFullName,
} from "./employeeHelpers";

const EMPTY_EMPLOYEE = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    dateOfBirth: "",
    registrationNumber: "",
    taxNumber: "",
    address: { address: "", address2: "", city: "", state: "", country: "", zip: "" },
};

/**
 * Add employee (`/settings/employees/new`) and edit employee details
 * (`/settings/employees/:id/edit`). Leaving with unsaved changes asks for confirmation.
 */
function EmployeeFormPage({ match }) {
    const employeeId = match?.params?.id;
    const isCreate = !employeeId;
    const { t } = useTranslation("settings");
    const history = useHistory();
    const toast = useToast();
    const { myCompanyData } = useUser();
    const [form] = Form.useForm();

    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(!isCreate);
    const [notFound, setNotFound] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [photoError, setPhotoError] = useState("");
    const unblockRef = useRef(null);
    // Live name for the avatar initials while typing.
    const watchedFirstName = Form.useWatch("firstName", form);
    const watchedLastName = Form.useWatch("lastName", form);

    const leavePath = isCreate ? EMPLOYEE_LIST_PATH : employeeDetailsPath(employeeId);

    const fetchEmployee = useCallback(async () => {
        try {
            const res = await apiClient.get(`api/employee/${employeeId}`);
            if (res?.data?.success && res.data.data) {
                setEmployee(res.data.data);
                form.setFieldsValue({ ...res.data.data, password: "" });
            } else {
                setNotFound(true);
            }
        } catch {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    }, [employeeId, form]);

    useEffect(() => {
        if (!isCreate) fetchEmployee();
    }, [isCreate, fetchEmployee]);

    useEffect(() => () => photoPreview && URL.revokeObjectURL(photoPreview), [photoPreview]);

    // Unsaved changes: confirm before any in-app navigation, and warn on tab close/refresh.
    useEffect(() => {
        if (!dirty) return undefined;

        const unblock = history.block((location) => {
            Modal.confirm({
                title: t("employees.discardChangesTitle"),
                content: t("employees.discardChangesDescription"),
                okText: t("employees.discard"),
                okButtonProps: { danger: true },
                cancelText: t("employees.keepEditing"),
                centered: true,
                onOk: () => {
                    unblock();
                    history.push(location);
                },
            });
            return false;
        });
        unblockRef.current = unblock;

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            // eslint-disable-next-line no-param-reassign -- required by browsers to show the prompt
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            unblock();
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [dirty, history, t]);

    const leave = (path = leavePath) => history.push(path);

    const finishAndLeave = (path) => {
        unblockRef.current?.();
        setDirty(false);
        history.push(path);
    };

    const handlePhotoChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
        setPhotoError("");
        setDirty(true);
    };

    const handleApiError = (data) => {
        const { message, errorSources } = data || {};
        if (message === "Validation Error" && applyValidationFieldErrors(form, errorSources)) return;
        toast.error({ message: getEmployeeErrorMessage({ message, errorSources }, t) });
    };

    const onFinish = async (values) => {
        if (isCreate && !photoFile) {
            setPhotoError(t("employees.employeeImageRequired"));
            return;
        }

        setSaving(true);
        const payload = { ...values };
        delete payload.company;
        if (!payload.password) delete payload.password;

        try {
            if (isCreate) {
                payload.company = myCompanyData?._id;
                payload.status = "active";
                payload.isDeleted = false;
                payload.displayPicture = await uploadFile(photoFile);
                const res = await apiClient.post("/api/account/create-employee", payload);
                if (!res?.data?.success) {
                    handleApiError(res?.data);
                    return;
                }
                toast.success({ message: t("employees.employeeCreated") });
                const createdId = res.data.data?.id ?? res.data.data?.employee?.id;
                finishAndLeave(createdId ? employeeDetailsPath(createdId) : EMPLOYEE_LIST_PATH);
                return;
            }

            payload.displayPicture = photoFile ? await uploadFile(photoFile) : employee?.displayPicture;
            const res = await apiClient.patch(`/api/employee/${employeeId}`, payload);
            if (!res?.data?.success) {
                handleApiError(res?.data);
                return;
            }
            toast.success({ message: t("employees.successfullyUpdated") });
            finishAndLeave(employeeDetailsPath(employeeId));
        } catch (err) {
            handleApiError(err?.response?.data);
        } finally {
            setSaving(false);
        }
    };

    const title = isCreate ? t("employees.addEmployee") : t("employees.editEmployee");
    const submitLabel = isCreate ? t("employees.createEmployee") : t("employees.saveChanges");

    const actions = (
        <>
            <Button shape="round" disabled={saving} onClick={() => leave()}>
                {t("cancel")}
            </Button>
            <Button type="primary" shape="round" loading={saving} onClick={() => form.submit()}>
                {submitLabel}
            </Button>
        </>
    );

    if (!isCreate && !loading && notFound) {
        return (
            <div className="flex min-w-0 flex-col gap-3">
                <EmployeePageHeader title={title} onBack={() => leave(EMPLOYEE_LIST_PATH)} />
                <div className="rounded-2xl border border-[#f0f0f0] bg-white py-16">
                    <Empty description={t("employees.notFound")}>
                        <Button shape="round" onClick={() => leave(EMPLOYEE_LIST_PATH)}>
                            {t("employees.backToEmployees")}
                        </Button>
                    </Empty>
                </div>
            </div>
        );
    }

    const previewEmployee = { ...employee, firstName: watchedFirstName, lastName: watchedLastName };

    return (
        <div className="flex min-w-0 flex-col gap-3">
            <EmployeePageHeader title={title} onBack={() => leave()} actions={loading ? null : actions} />

            {loading ? (
                <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(280px,340px)_minmax(0,1fr)]">
                    <div className="flex justify-center rounded-2xl border border-[#f0f0f0] bg-white p-8">
                        <Skeleton.Avatar active size={112} />
                    </div>
                    <div className="rounded-2xl border border-[#f0f0f0] bg-white p-8">
                        <Skeleton active paragraph={{ rows: 10 }} />
                    </div>
                </div>
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onValuesChange={() => setDirty(true)}
                    onFinishFailed={() => {
                        if (isCreate && !photoFile) setPhotoError(t("employees.employeeImageRequired"));
                    }}
                    autoComplete="off"
                    requiredMark={customizeRequiredMark}
                    initialValues={isCreate ? EMPTY_EMPLOYEE : undefined}
                    scrollToFirstError>
                    <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(280px,340px)_minmax(0,1fr)]">
                        <aside className="flex flex-col gap-4 self-start rounded-2xl border border-[#f0f0f0] bg-white px-6 py-8 xl:sticky xl:top-4">
                            <EmployeePhotoUpload
                                preview={photoPreview || employee?.displayPicture}
                                employee={previewEmployee}
                                onChange={handlePhotoChange}
                                error={photoError}
                            />
                            {!isCreate && (
                                <div className="flex flex-col items-center gap-2 border-t border-[#f0f0f0] pt-4 text-center">
                                    <span className="fontbold break-words text-base text-[#121212]">
                                        {getEmployeeFullName(employee)}
                                    </span>
                                    <EmployeeStatusTag employee={employee} />
                                </div>
                            )}
                        </aside>

                        <div className="flex min-w-0 flex-col gap-3">
                            <EmployeeFormFields t={t} companyName={myCompanyData?.name || ""} isCreate={isCreate} />
                            <div className="flex flex-wrap justify-end gap-2 pb-2">{actions}</div>
                        </div>
                    </div>
                </Form>
            )}
        </div>
    );
}

EmployeeFormPage.propTypes = {
    match: PropTypes.shape({ params: PropTypes.shape({ id: PropTypes.string }) }),
};

export default EmployeeFormPage;
